import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useNavigate } from 'react-router-dom';

import { fetchFailedRuns } from '../../services/activeRunsService';
import {
  getFailedRunsDateRange,
  SUMMARY_LIMIT,
  toDateTimeLocalValue,
} from './summary/utils/runSummaryUtils';
import { AppiumAutomationPagesHeader } from '../appiumAutomationPagesComponents';
import { runSummaryStyles } from './summary/styles/runSummaryStyles';
import useRunSummaryData from './summary/hooks/useRunSummaryData';
import RunSummaryAgentDrilldown from './summary/RunSummaryAgentDrilldown';

export default function ActiveRunsFailedRunsPage() {
  const navigate = useNavigate();
  const [errorLoadingFailedRuns, setErrorLoadingFailedRuns] = useState(null);
  const [loadingFailedRuns, setLoadingFailedRuns] = useState(false);
  const [runs, setRuns] = useState([]);
  const [reportData, setReportData] = useState([]);
  const { filters, filtersVersion, setFilterValue, resetFilters, deviceTypeSelected } =
    useRunSummaryData();

  useEffect(() => {
    let mounted = true;

    const loadFailedRuns = async () => {
      try {
        setLoadingFailedRuns(true);
        setErrorLoadingFailedRuns(null);
        const response = await fetchFailedRuns(
          SUMMARY_LIMIT,
          filters.deviceId || null,
          filters.startDate || null,
          filters.endDate || null,
          filters.androidBuildVersion || null,
          filters.iosBuildVersion || null,
        );
        if (!mounted) return;
        const nextRuns = response?.runs || [];
        setRuns(nextRuns);
        setReportData(response.reportData);

        const { startDate, endDate } = getFailedRunsDateRange(nextRuns);
        const nextFilters = {};

        if (!filters.startDate && startDate) {
          nextFilters.startDate = toDateTimeLocalValue(startDate);
        }

        if (!filters.endDate && endDate) {
          nextFilters.endDate = toDateTimeLocalValue(endDate);
        }

        if (Object.keys(nextFilters).length > 0) {
          setFilterValue(nextFilters);
        }
      } catch (err) {
        if (!mounted) return;
        setErrorLoadingFailedRuns(
          err?.response?.data?.message ||
            err?.response?.data?.detail ||
            err?.message ||
            'Failed to load failed runs.',
        );
        setRuns([]);
      } finally {
        if (mounted) setLoadingFailedRuns(false);
      }
    };

    loadFailedRuns();

    return () => {
      mounted = false;
    };
  }, [filters, setFilterValue]);

  const handleAndroidVersionChange = (event) => {
    setFilterValue({ androidBuildVersion: event.target.value });
  };

  const handleIosVersionChange = (event) => {
    setFilterValue({ iosBuildVersion: event.target.value });
  };

  const handleFilterChangeStartDate = (event) => {
    setFilterValue({ startDate: event.target.value });
  };

  const handleFilterChangeEndDate = (event) => {
    setFilterValue({ endDate: event.target.value });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3, pt: 0 }}>
      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/active-runs/summary')}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          Back to Runs Summary
        </Button>
      </Box>
      <AppiumAutomationPagesHeader />
      <Grid container spacing={3}>
        <Grid item>
          <Box mb={2}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <HighlightOffIcon color="error" />
              <Typography variant="h6">
                {deviceTypeSelected === 'ios' ? 'iOS Failed Runs' : 'Android Failed Runs'} (
                {runs.length})
              </Typography>
            </Box>
          </Box>
          <Box sx={runSummaryStyles.filterGrid}>
            {deviceTypeSelected === 'android' ? (
              <Box component="span" sx={runSummaryStyles.filterControl}>
                <TextField
                  select
                  label="Android Build"
                  size="small"
                  value={filters.androidBuildVersion}
                  onChange={handleAndroidVersionChange}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => selected || 'All Android Builds',
                  }}
                  sx={runSummaryStyles.filterControl}
                >
                  <MenuItem value="">All Android Versions</MenuItem>
                  {filtersVersion.android?.map((version) => (
                    <MenuItem key={`android-${version}`} value={version}>
                      {version}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            ) : (
              <Box component="span" sx={runSummaryStyles.filterControl}>
                <TextField
                  select
                  label="iOS Build"
                  size="small"
                  value={filters.iosBuildVersion}
                  onChange={handleIosVersionChange}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => selected || 'All iOS Builds',
                  }}
                  sx={runSummaryStyles.filterControl}
                >
                  <MenuItem value="">All iOS Versions</MenuItem>
                  {filtersVersion.ios?.map((version) => (
                    <MenuItem key={`ios-${version}`} value={version}>
                      {version}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            <TextField
              label="Start Date"
              type="datetime-local"
              size="small"
              value={filters.startDate}
              onChange={handleFilterChangeStartDate}
              InputLabelProps={{ shrink: true }}
              sx={runSummaryStyles.filterControl}
            />
            <TextField
              label="End Date"
              type="datetime-local"
              size="small"
              value={filters.endDate}
              onChange={handleFilterChangeEndDate}
              InputLabelProps={{ shrink: true }}
              sx={runSummaryStyles.filterControl}
            />
            <Button variant="outlined" onClick={resetFilters} sx={runSummaryStyles.resetButton}>
              Reset
            </Button>
          </Box>

          {loadingFailedRuns && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Loading failed runs...</Typography>
              </Stack>
            </Paper>
          )}

          {errorLoadingFailedRuns && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorLoadingFailedRuns}
            </Alert>
          )}

          {!loadingFailedRuns && !errorLoadingFailedRuns && runs.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No failed runs match the selected filters
              </Typography>
            </Paper>
          )}

          {!loadingFailedRuns && !errorLoadingFailedRuns && (
            <RunSummaryAgentDrilldown items={reportData} runs={runs} forFailedRuns />
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
